import { type MutableRefObject, useCallback } from 'react';
import type { IClientPublishOptions, IClientSubscribeOptions, MqttClient } from 'mqtt';

export interface EmqxMessaging {
    publish: (topic: string, message: string, options?: IClientPublishOptions) => void;
    subscribe: (
        topic: string,
        options?: IClientSubscribeOptions,
        onSettled?: (error?: Error | null) => void
    ) => void;
    unsubscribe: (topic: string, options?: IClientSubscribeOptions) => void;
}

/** Stable publish, subscribe and unsubscribe functions over the current EMQX client. */
export function useEmqxMessaging(
    emqxClientRef: MutableRefObject<MqttClient | null>,
    isEmqxConnectedRef: MutableRefObject<boolean>
): EmqxMessaging {
    // Stable publish function - uses EMQX
    const publish = useCallback(
        (topic: string, message: string, options: IClientPublishOptions = { qos: 1 }) => {
            // Use EMQX if connected
            if (emqxClientRef.current && isEmqxConnectedRef.current) {
                emqxClientRef.current.publish(topic, message, options, (err) => {
                    if (err) {
                        console.error(`[EMQX] Failed to publish to ${topic}: `, err);
                    }
                });
                return;
            }

            console.warn('No MQTT broker connected to publish message');
        },
        [emqxClientRef, isEmqxConnectedRef] // Stable: the refs never change identity
    );

    // Stable subscribe function - uses EMQX
    const subscribe = useCallback(
        (
            topic: string,
            options: IClientSubscribeOptions = { qos: 1 },
            onSettled?: (error?: Error | null) => void
        ) => {
            // Call onSettled only after a successful SUBACK. A failed subscribe must
            // not look ready, or callers publish requests the broker will not deliver.
            if (emqxClientRef.current && isEmqxConnectedRef.current) {
                emqxClientRef.current.subscribe(topic, options, (err) => {
                    if (err) {
                        console.error(`[EMQX] Failed to subscribe to ${topic}: `, err);
                        onSettled?.(err);
                        return;
                    }
                    onSettled?.();
                });
                return;
            }
            onSettled?.(new Error('No MQTT broker connected'));
        },
        [emqxClientRef, isEmqxConnectedRef]
    ); // Stable: the refs never change identity

    // Stable unsubscribe function - uses EMQX
    const unsubscribe = useCallback(
        (topic: string, options: IClientSubscribeOptions = { qos: 1 }) => {
            // Use EMQX if connected
            if (emqxClientRef.current && isEmqxConnectedRef.current) {
                emqxClientRef.current.unsubscribe(topic, options, (err) => {
                    if (err) {
                        console.error(`[EMQX] Failed to unsubscribe from ${topic}: `, err);
                    }
                });
            }
        },
        [emqxClientRef, isEmqxConnectedRef]
    ); // Stable: the refs never change identity

    return { publish, subscribe, unsubscribe };
}
